import React, { useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { NoIndex } from '../components/SEO/NoIndex';

// Five cleaner background options — all keeping the warm CoRegulate identity
// but dialing back the peach saturation. The current background (`current`)
// is included as a baseline for comparison.
const BACKGROUNDS = [
    {
        id: 'current',
        name: 'Current',
        sub: 'Full peach — the present site',
        style: { background: 'linear-gradient(to bottom, #FBE0C3 0%, #FBE0C3 60%, #f5d4b3 100%)' },
        accent: '#FBE0C3',
        notes: 'Saturated peach throughout. High brand presence but can feel heavy in long sessions.',
    },
    {
        id: 'soft-wash',
        name: 'Soft Wash',
        sub: 'A whisper of peach',
        style: { background: 'linear-gradient(180deg, #FDF6EE 0%, #FBEFE0 100%)' },
        accent: '#FBEFE0',
        notes: 'Mostly cream with a very light peach drift. Calm, modern, easy on the eyes.',
    },
    {
        id: 'paper-cream',
        name: 'Paper Cream',
        sub: 'Editorial off-white with warmth',
        style: { background: '#FBF6EE' },
        accent: '#FBF6EE',
        notes: 'A clean, warm off-white. Lets the peach accent colors and cards do the talking.',
    },
    {
        id: 'dawn-fade',
        name: 'Dawn Fade',
        sub: 'Subtle vertical gradient',
        style: { background: 'linear-gradient(180deg, #FEF8F1 0%, #FBF1E4 45%, #F8E6D0 100%)' },
        accent: '#FBF1E4',
        notes: 'Lightest at the top, gently warming downward. Calm with a touch of depth.',
    },
    {
        id: 'linen-blush',
        name: 'Linen Blush',
        sub: 'Warm neutral, faint blush accent',
        style: {
            background:
                "#F8F2E9 radial-gradient(circle at 90% -10%, rgba(255,187,152,0.18), transparent 50%)",
        },
        accent: '#F8F2E9',
        notes: 'A neutral linen base with a faint blush highlight in the upper-right corner. Editorial feel.',
    },
    {
        id: 'morning-mist',
        name: 'Morning Mist',
        sub: 'Off-white with a single warm spot',
        style: {
            background:
                'radial-gradient(ellipse at 50% 0%, #FDEFDC 0%, #FBF6EE 35%, #FBF6EE 100%)',
        },
        accent: '#FDEFDC',
        notes: 'Soft sunlight halo at the top, fading into clean off-white. Gentle but distinctive.',
    },
];


function MiniDashboard({ accent }) {
    // Simulated app card so the user can see UI sitting on the background.
    return (
        <div className="space-y-3 max-w-sm mx-auto">
            <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/80 p-4 shadow-sm">
                <p className="text-xs text-[#344648]/60">Good morning,</p>
                <p className="font-serif text-2xl text-[#344648] leading-tight mt-0.5">Welcome back, Maya</p>
                <p className="text-xs text-[#344648]/70 mt-1">Let&apos;s check in with your body</p>
            </div>
            <div className="rounded-xl bg-white/60 backdrop-blur border border-white/70 p-3 flex items-center gap-3">
                <span
                    className="w-9 h-9 rounded-lg flex-shrink-0"
                    style={{ background: accent }}
                />
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#344648] truncate">Calm Cycle</p>
                    <p className="text-xs text-[#344648]/60 truncate">2 minute breath reset</p>
                </div>
            </div>
            <div className="rounded-xl bg-white/60 backdrop-blur border border-white/70 p-3 flex items-center gap-3">
                <span
                    className="w-9 h-9 rounded-lg flex-shrink-0"
                    style={{ background: '#FFBB98' }}
                />
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#344648] truncate">Sage</p>
                    <p className="text-xs text-[#344648]/60 truncate">Your AI guide is here</p>
                </div>
            </div>
        </div>
    );
}


export default function BackgroundPreview() {
    const [selected, setSelected] = useState(null);

    return (
        <div className="min-h-screen bg-[#FBF6EE] py-12 px-4 md:px-8" data-testid="bg-preview-page">
            <NoIndex />
            <div className="max-w-7xl mx-auto">
                <header className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur border border-white/80 mb-5">
                        <Sparkles className="w-4 h-4 text-[#FFBB98]" />
                        <span className="text-sm font-medium text-[#344648]">Background options</span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-[#344648] tracking-tight">
                        Pick a <span className="text-[#FFBB98]">cleaner</span> background
                    </h1>
                    <p className="text-base text-[#344648]/70 mt-4 max-w-2xl mx-auto">
                        Five lighter alternatives, all in keeping with the warm CoRegulate identity.
                        Each card shows how your dashboard would look against that background.
                    </p>
                </header>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {BACKGROUNDS.map((bg) => (
                        <button
                            key={bg.id}
                            onClick={() => setSelected(bg.id)}
                            className={`
                                group relative rounded-3xl overflow-hidden border-2 text-left transition-all
                                ${selected === bg.id
                                    ? 'border-[#344648] shadow-2xl scale-[1.02]'
                                    : 'border-white/80 shadow-md hover:shadow-xl hover:-translate-y-1'}
                            `}
                            style={{ minHeight: 480 }}
                            data-testid={`bg-option-${bg.id}`}
                        >
                            {/* The background itself fills the card */}
                            <div className="absolute inset-0" style={bg.style} />

                            {/* Content */}
                            <div className="relative h-full p-6 flex flex-col">
                                <div className="flex items-start justify-between mb-5">
                                    <div>
                                        <h3 className="font-serif text-2xl text-[#344648]">{bg.name}</h3>
                                        <p className="text-xs text-[#344648]/65 mt-0.5">{bg.sub}</p>
                                    </div>
                                    {selected === bg.id && (
                                        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#344648] text-white text-xs font-medium">
                                            <Check className="w-3 h-3" /> Selected
                                        </span>
                                    )}
                                </div>

                                <div className="flex-1 flex items-center">
                                    <MiniDashboard accent={bg.accent} />
                                </div>

                                <p className="text-xs text-[#344648]/65 mt-5 leading-relaxed">
                                    {bg.notes}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Sticky decision banner */}
                {selected && (
                    <div className="mt-10 sticky bottom-6 z-10">
                        <div className="rounded-3xl bg-[#344648] text-white p-5 md:p-6 flex items-center justify-between gap-4 shadow-2xl">
                            <div className="min-w-0">
                                <p className="text-xs uppercase tracking-wider text-white/60">You picked</p>
                                <p className="font-serif text-2xl truncate">
                                    {BACKGROUNDS.find((b) => b.id === selected)?.name}
                                </p>
                            </div>
                            <div className="flex-shrink-0 px-4 py-2 rounded-full bg-white/15 text-sm">
                                Reply with this name and I&apos;ll apply it across the site
                            </div>
                        </div>
                    </div>
                )}

                <footer className="text-center mt-16 text-xs text-[#344648]/50">
                    Tip: this preview page lives at <code className="px-1.5 py-0.5 rounded bg-white/60">/background-preview</code> — share it with anyone for feedback.
                </footer>
            </div>
        </div>
    );
}
